/**
 * The Customer Statement contract — mirrors the Spring DTOs in
 * module/customerstatement/dto, field for field.
 */

export interface StatementRequest {
  companyId: number;
  /** One customer, or 0 for every customer with something outstanding. */
  customerId: number;
  /** Legacy "Include Credit Note". */
  includeCreditNotes: boolean;
  /** Legacy "Include Date": use fromDate..toDate instead of the company cutoff. */
  useDateRange: boolean;
  fromDate: string | null;
  toDate: string | null;
}

export type StatementLineKind = 'INVOICE' | 'CREDIT_NOTE';

export interface StatementLine {
  kind: StatementLineKind;
  date: string;
  documentNo: string;
  reference: string | null;
  description: string | null;
  debit: number;
  credit: number;
  balance: number;
}

export interface AgeingBucket {
  year: number;
  month: number;
  monthName: string;
  label: string;
  amount: number;
}

export interface CustomerStatement {
  customerId: number;
  customerName: string;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  phone: string | null;
  attn: string | null;
  accountCode: string | null;
  terms: string | null;
  currency: string | null;
  emails: string[];
  statementDate: string;
  lines: StatementLine[];
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
  ageing: AgeingBucket[];
  openingBalance: number;
  overdueAmount: number;
  overdueAsOf: string | null;
}

export interface StatementResult {
  statements: CustomerStatement[];
  customerCount: number;
  lineCount: number;
  cutoffDate: string;
  periodFrom: string;
  periodTo: string | null;
  ageingFrom: string;
  ageingTo: string;
}

export interface StatementSendRequest extends StatementRequest {
  /** Comma or semicolon separated, as typed in the Emails box. */
  emails: string;
  /** '' for the plain statement, 'Reminder 1' or 'Reminder 2'. */
  reminder: string;
  /** CC as edited on the preview; left out keeps the configured CC, '' sends none. */
  cc?: string;
  /** Subject as edited on the preview; left out uses the wording's own. */
  subject?: string;
  /** HTML body as edited on the preview; left out uses the wording's own template. */
  body?: string;
  /** What to attach; left out attaches the PDF. */
  attach?: StatementAttach;
}

/** The statement file(s) a mail carries. */
export type StatementAttach = 'PDF' | 'EXCEL' | 'BOTH';

export const ATTACH_OPTIONS: { value: StatementAttach; label: string }[] = [
  { value: 'PDF', label: 'PDF' },
  { value: 'EXCEL', label: 'Excel' },
  { value: 'BOTH', label: 'PDF + Excel' },
];

/** The statement mail as Send would build it, before anything is sent. */
export interface StatementMailPreview {
  customerName: string;
  reminder: string;
  subject: string;
  body: string;
  to: string[];
  cc: string[];
  attachmentName: string;
  /** The Excel workbook's file name, for when Excel is attached. */
  excelAttachmentName: string;
  overdueAmount: number;
  currency: string | null;
}

export interface StatementSendResult {
  customerName: string;
  subject: string;
  sentTo: string[];
  cc: string[];
  attachmentName: string;
  overdueAmount: number;
  currency: string | null;
  sentAt: string;
}

// ── Send Statements workspace (bulk run) ─────────────────────────────────────

export type MailJobStatus = 'QUEUED' | 'RUNNING' | 'DONE' | 'CANCELLED' | 'FAILED';
export type MailItemStatus = 'PENDING' | 'SENDING' | 'SENT' | 'FAILED' | 'SKIPPED' | 'CANCELLED';

export interface StatementMailRecipient {
  customerId: number;
  /** As in the row's box. Empty string = the operator cleared it, so the customer is skipped. */
  emails: string;
}

/** One run: the statement filters, one wording, and the customers with their addresses. */
export interface StatementMailJobRequest extends StatementRequest {
  customers: StatementMailRecipient[];
  reminder: string;
  /** The same file(s) for every customer; left out attaches the PDF. */
  attach?: StatementAttach;
}

export interface StatementMailJobItem {
  customerId: number;
  customerName: string;
  emails: string[];
  status: MailItemStatus;
  error: string | null;
  sentAt: string | null;
  subject: string | null;
  attachmentName: string | null;
  overdueAmount: number | null;
  currency: string | null;
}

/** A snapshot of a run, polled while it goes. */
export interface StatementMailJob {
  id: string;
  companyId: number;
  status: MailJobStatus;
  reminder: string;
  /** PDF, EXCEL or BOTH (absent from a server that predates Excel). */
  attach?: StatementAttach;
  requestedBy: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** Why the run itself stopped (a refused login); a refused mail is a FAILED item instead. */
  error: string | null;
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  cancelled: number;
  pending: number;
  items: StatementMailJobItem[];
}

export const isJobActive = (job: StatementMailJob | null | undefined): boolean =>
  job?.status === 'QUEUED' || job?.status === 'RUNNING';

/** When a customer last received a statement mail, from the send log. */
export interface StatementLastSent {
  customerId: number;
  sentAt: string;
  /** '' = Statement of Account, else 'Reminder 1' / 'Reminder 2'. */
  reminder: string;
  sentTo: string;
  sentBy: string | null;
}

// ── Replies from customers, and answering them ──────────────────────────────

/** Per customer: the latest reply and how many there are — the "Reply" column. */
export interface StatementReplySummary {
  customerId: number;
  replyCount: number;
  unreadCount: number;
  lastReplyId: number;
  lastReplyAt: string;
  lastFrom: string;
  lastFromName: string | null;
  lastSubject: string | null;
  lastSnippet: string | null;
}

export interface ConversationAttachment {
  id: number;
  fileName: string;
  contentType: string | null;
  sizeBytes: number;
}

/** One mail in the conversation: SENT = ours (from the send log), REPLY = theirs (from the mailbox). */
export interface ConversationEntry {
  kind: 'SENT' | 'REPLY';
  id: number;
  at: string;
  from: string | null;
  to: string | null;
  cc: string | null;
  subject: string | null;
  /** Readable text; a reply's HTML never reaches the screen. */
  body: string | null;
  status: string | null;
  error: string | null;
  sentBy: string | null;
  attachmentName: string | null;
  unread: boolean;
  attachments: ConversationAttachment[];
  /** For a reply: where Reply All would go. */
  replyAllHint: string[];
}

export interface StatementConversation {
  customerId: number;
  customerName: string;
  /** The customer master's statement addresses, the default for a fresh mail. */
  emails: string[];
  /** Addresses earlier mails went to (or replies came from) that are not on the record — one-off ones. */
  previousAddresses: string[];
  entries: ConversationEntry[];
}

export type ReplyMode = 'REPLY' | 'REPLY_ALL';

export interface ReplySendRequest {
  companyId: number;
  customerId: number;
  /** The customer mail being answered; null for a fresh mail. */
  replyToId: number | null;
  mode: ReplyMode;
  /** Extra or overriding To addresses, comma-separated. */
  to?: string;
  /** Blank = "Re: " + the original subject. */
  subject?: string;
  body: string;
  attachStatement: boolean;
}

export interface ReplySendResult {
  subject: string;
  sentTo: string[];
  cc: string[];
  attachmentName: string | null;
  sentAt: string;
}

/** The header bell: unread replies, newest first. `allowed` false = this role is not on the notify list. */
export interface ReplyInbox {
  allowed: boolean;
  count: number;
  items: ReplyInboxItem[];
}

export interface ReplyInboxItem {
  replyId: number;
  customerId: number;
  customerName: string;
  fromAddress: string;
  fromName: string | null;
  subject: string | null;
  snippet: string | null;
  receivedAt: string;
  /** The mail of ours it answers. */
  statementSubject: string | null;
  statementSentAt: string | null;
}

export interface ReplySyncResult {
  mailbox: string;
  folder: string;
  scanned: number;
  imported: number;
  ranAt: string | null;
  error: string | null;
  running: boolean;
}

/**
 * The legacy Reminder dropdown. Only the first two have their own mail
 * subject and body in the legacy sender; 3–5 fell through to the default
 * statement mail. Listed as they were so the screen matches; the send step
 * treats 3–5 as "Statement".
 */
export const REMINDER_OPTIONS = [
  { value: '', label: 'Statement of Account' },
  { value: 'Reminder 1', label: 'Reminder 1 — friendly' },
  { value: 'Reminder 2', label: 'Reminder 2 — overdue' },
  // The last notice: one fixed subject and body, its deadline in the wording.
  { value: 'Final Reminder', label: 'Final Reminder — pay before 25 Sep 2026' },
  // The festive request: one fixed subject and body, its deadline in the wording.
  { value: 'Deepavali Festive', label: 'Deepavali Festive — pay by 25 Oct 2026' },
] as const;
